import { useCanvasElements } from '@/hooks/useCanvasElements';
import VisualCanvas from '@/components/VisualCanvas';
import Toolbar from '@/components/Toolbar';
import ControlSidebar from '@/components/ControlSidebar';
import PromptPreview from '@/components/PromptPreview';

import { Zap, RotateCcw, Moon, Sun, Languages, Camera, User } from 'lucide-react';
import { useState, useCallback, useEffect } from 'react';
import { enhanceService } from '@/ai/enhanceService';
import { useGlobalPromptStore } from '@/store/promptStore';
import type { PromptSettingsPreset } from '@/ai/grokPromptService';
import { normalizeSubject } from '@/lib/subjectUtils';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { parseDescriptionSettings, hasPatchChanges } from '@/lib/textToSettingsSync';

const FB_URL = 'https://www.facebook.com/profile.php?id=61587106286387';

const extractSubjectFromPrompt = (value: string): string | null => {
  const text = value.trim();
  if (!text) return null;
  const match = text.match(/(?:main subject|subject|hero|character)\s*:\s*([^.,\n]+)/i);
  const candidate = (match?.[1] || text.split(/[,.\n]/)[0] || '').trim();
  return candidate || null;
};

const Index = () => {
  const [enhanceBusy, setEnhanceBusy] = useState(false);
  const [grokDetectedSubject, setGrokDetectedSubject] = useState<string | null>(null);
  const { finalPrompt, setFinalPrompt } = useGlobalPromptStore();
  const { theme, toggleTheme } = useTheme();
  const { lang, toggleLang, t } = useLanguage();


  const {
    elements, aspectRatio, setAspectRatio,
    selectedId, setSelectedId, selected,
    addElement, ensureElement, moveElement, scaleElement,
    updateProperty, removeElement,
    prompt, conflicts, inferred, locked, vfxLayers, vfxIntensity, styleTendencies,
    saveToHistory,
    sceneDescription, setSceneDescription,
    stylePreset, setStylePreset,
    palettePreset, setPalettePreset,
    customPalette, setCustomPalette,
    promptFormat, setPromptFormat,
    mode, setMode,
    globalSettings, updateGlobal,
    magneticGuides,
    resetAll,
    fixPromptIssueFromText,
    fixAllSceneIssues,
    handleImageAnalysis,
    applyPromptDraft,
  } = useCanvasElements();

  // تعريف effectivePrompt مبكرًا
  const effectivePrompt = prompt || sceneDescription.trim();

  // تعريف applyPromptDraftCallback مبكرًا
  const applyPromptDraftCallback = useCallback((draft: string) => {
    // forward to hook's helper
    applyPromptDraft(draft);
  }, [applyPromptDraft]);

  // Fix button handler: إصلاح المشاكل المنطقية في البرومبت
  const [fixBusy, setFixBusy] = useState(false);
  const handleFix = useCallback(async () => {
    if (!effectivePrompt || effectivePrompt.startsWith('Place elements') || fixBusy) return;
    setFixBusy(true);
    try {
      // إصلاح المشاكل المنطقية باستخدام Grok/Gemini
      const { fixPromptWithGrokDirect } = await import('@/ai/grokPromptService');
      const result = await fixPromptWithGrokDirect(effectivePrompt);
      if (result.ok && result.prompt) {
        // تحديث البرومبت في المحرر
        applyPromptDraftCallback(result.prompt);
        // إعادة التحليل وتحديث لوحة المشاكل
        if (typeof window !== 'undefined' && window.testFixAPI) {
          window.testFixAPI();
        }
      }
    } finally {
      setFixBusy(false);
    }
  }, [effectivePrompt, fixBusy, applyPromptDraftCallback]);
  const elementCounts = {
    light: elements.filter(e => e.type === 'light').length,
    camera: elements.filter(e => e.type === 'camera').length,
    character: elements.filter(e => e.type === 'character').length,
  };

  // ── Text-to-Settings Sync Layer ──
  // Parses the user's RAW input text (Arabic/English) and applies detected settings
  const handleRawTextSubmit = useCallback((rawText: string) => {
    const patch = parseDescriptionSettings(rawText);
    if (!hasPatchChanges(patch)) return;

    // Apply aspect ratio
    if (patch.aspectRatio) {
      setAspectRatio(patch.aspectRatio);
    }

    // Apply camera settings
    if (patch.camera && Object.keys(patch.camera).length) {
      const cameraId = ensureElement('camera');
      for (const [key, value] of Object.entries(patch.camera)) {
        updateProperty(cameraId, key, value);
      }
    }

    // Apply light settings
    if (patch.light && Object.keys(patch.light).length) {
      const lightId = ensureElement('light');
      for (const [key, value] of Object.entries(patch.light)) {
        updateProperty(lightId, key, value);
      }
    }

    // Apply global settings
    if (patch.global && Object.keys(patch.global).length) {
      for (const [key, value] of Object.entries(patch.global)) {
        updateGlobal(key, value);
      }
    }
  }, [setAspectRatio, ensureElement, updateProperty, updateGlobal]);


  useEffect(() => {
    if (!sceneDescription.trim()) return;
    handleRawTextSubmit(sceneDescription);
  }, [sceneDescription, handleRawTextSubmit]);


  const canvasSubject = normalizeSubject(
    elements
      .filter((el) => el.type === 'character')
      .map((el) => String(el.properties.subject || '').trim())
      .find(Boolean) || null,
    null,
  );

  const promptSubject = normalizeSubject(grokDetectedSubject, extractSubjectFromPrompt(effectivePrompt));



  useEffect(() => {
    setFinalPrompt(effectivePrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePrompt]);

  useEffect(() => {
    if (!promptSubject) return;
    const targetCharacter = elements.find((el) => el.type === 'character');
    if (!targetCharacter) return;
    const current = String(targetCharacter.properties.subject || '').trim();
    if (current.toLowerCase() === promptSubject.toLowerCase()) return;
    updateProperty(targetCharacter.id, 'subject', promptSubject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptSubject]);



  const applySettingsPreset = useCallback((preset: PromptSettingsPreset) => {
    setStylePreset(preset.style || 'none');
    setPalettePreset(preset.palette || 'none');
    updateGlobal('renderQuality', preset.renderQuality || '4k');
    updateGlobal('ruleOfThirds', /rule of thirds/i.test(preset.composition));
    updateGlobal('centeredCinematic', /centered/i.test(preset.composition));
    updateGlobal('atmosphericDensity', /atmospheric|fog|volumetric/i.test(preset.lighting) ? 45 : 20);
    updateGlobal('godRaysGlobal', /volumetric/i.test(preset.lighting) ? 40 : 10);

    const cameraId = ensureElement('camera');
    updateProperty(cameraId, 'shotType', /close-up/i.test(preset.camera) ? 'close_up' : /wide/i.test(preset.camera) ? 'wide' : 'medium');
    updateProperty(cameraId, 'perspective', /low angle/i.test(preset.camera) ? 'low_angle' : /top/i.test(preset.camera) ? 'birds_eye' : 'eye_level');
    updateProperty(cameraId, 'lens', /85mm/i.test(preset.lens) ? '85mm' : /35mm/i.test(preset.lens) ? '35mm' : '50mm');

    const lightId = ensureElement('light');
    updateProperty(lightId, 'lightStyle', /low key/i.test(preset.lighting) ? 'dramatic' : 'natural');
    updateProperty(lightId, 'lightPreset', /night|moon/i.test(preset.lighting) ? 'blue_hour' : 'golden_hour');
    updateProperty(lightId, 'intensity', /dramatic|low key/i.test(preset.lighting) ? '70' : '55');
  }, [ensureElement, setPalettePreset, setStylePreset, updateGlobal, updateProperty]);

  // ...existing code...

  const handleEnhance = useCallback(async () => {
    if (!effectivePrompt || effectivePrompt.startsWith('Place elements') || enhanceBusy) return;
    setEnhanceBusy(true);
    try {
      // تحسين البرومبت باستخدام Grok/Gemini
      const { enhancePromptWithGrokDirect } = await import('@/ai/grokPromptService');
      const result = await enhancePromptWithGrokDirect(effectivePrompt);
      if (result.ok && result.prompt) {
        // تحديث البرومبت في المحرر
        applyPromptDraftCallback(result.prompt);
        // إعادة التحليل وتحديث لوحة التحليل
        if (typeof window !== 'undefined' && window.testEnhanceAPI) {
          window.testEnhanceAPI();
        }
      }
    } finally {
      setEnhanceBusy(false);
    }
  }, [effectivePrompt, enhanceBusy, applyPromptDraftCallback]);

  return (
    <div className="studio-shell h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <header className="studio-header relative flex items-center justify-between px-4 py-2.5 border-b border-border/30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/25 flex items-center justify-center glow-teal-strong">
            <Zap className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-foreground tracking-tight leading-none">{t('app.title')}</h1>
            <p className="text-[8px] font-mono text-muted-foreground/40 tracking-[0.15em] mt-0.5">{t('app.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-primary/80 pulse-dot" />
            <span className="text-[8px] font-mono text-muted-foreground/40">{t('app.live')}</span>
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="w-7 h-7 rounded-lg border border-border/30 bg-muted/20 flex items-center justify-center hover:border-primary/40 hover:bg-primary/10 transition-all group"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? (
              <Sun className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary transition-colors" />
            ) : (
              <Moon className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary transition-colors" />
            )}
          </button>

          {/* Language Toggle */}
          <button
            onClick={toggleLang}
            className="h-7 px-2 rounded-lg border border-border/30 bg-muted/20 flex items-center justify-center gap-1 hover:border-primary/40 hover:bg-primary/10 transition-all group"
            title={lang === 'en' ? 'التبديل للعربية' : 'Switch to English'}
          >
            <Languages className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary transition-colors" />
            <span className="text-[9px] font-mono text-muted-foreground/50 group-hover:text-primary transition-colors">{lang === 'en' ? 'AR' : 'EN'}</span>
          </button>

          {/* Clean / Reset button */}
          <button
            onClick={resetAll}
            className="flex items-center gap-1 px-2 py-1 rounded-lg border border-border/30 bg-muted/20 hover:border-destructive/40 hover:bg-destructive/10 transition-all group"
            title={t('misc.resetAll')}
          >
            <RotateCcw className="w-3 h-3 text-muted-foreground/50 group-hover:text-destructive transition-colors" />
            <span className="text-[9px] font-mono text-muted-foreground/50 group-hover:text-destructive transition-colors">{t('app.clean')}</span>
          </button>
          {/* Facebook link */}
          <a
            href={FB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-7 h-7 rounded-lg border border-border/30 bg-muted/20 flex items-center justify-center hover:border-primary/40 hover:bg-primary/10 transition-all group"
            title={t('misc.followFb')}
          >
            <svg className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary transition-colors" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
          <span className="text-[8px] font-mono text-muted-foreground/20 uppercase tracking-widest">v5.0</span>
        </div>
        <div className="absolute left-1/2 -translate-x-1/2 text-[10px] font-mono uppercase tracking-[0.28em] text-muted-foreground/35 whitespace-nowrap">
          By <span className="text-primary/85 font-semibold tracking-[0.14em]">Omar Megahid</span>
        </div>
      </header>


      {/* Toolbar */}
      <Toolbar
        graphPrompt={prompt}
        aspectRatio={aspectRatio} onAspectChange={setAspectRatio}
        onAddElement={addElement}
        stylePreset={stylePreset} onStyleChange={setStylePreset}
        palettePreset={palettePreset} onPaletteChange={setPalettePreset}
        customPalette={customPalette} onCustomPaletteChange={setCustomPalette}
        promptFormat={promptFormat} onFormatChange={setPromptFormat}
        sceneDescription={sceneDescription} onSceneChange={setSceneDescription}
        onRawTextSubmit={handleRawTextSubmit}
        mode={mode} onModeChange={setMode}
        elementCounts={elementCounts}
        onImageAnalysis={handleImageAnalysis}
      />

      {/* Main */}
      <div className="studio-main flex-1 flex min-h-0 overflow-hidden">
        <ControlSidebar
          mode={mode}
          selectedElement={selected}
          globalSettings={globalSettings}
          elements={elements}
          onUpdateElement={updateProperty}
          onUpdateGlobal={updateGlobal}
          onRemoveElement={removeElement}
          onEnsureElement={ensureElement}
        />

        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          {/* Element buttons above canvas */}
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border/20">
            <button onClick={() => addElement('light')} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border/30 text-[10px] font-mono text-muted-foreground transition-all hover:bg-primary/10 hover:text-primary hover:border-primary/40">
              <Sun className="w-3 h-3" /> Light
              {elementCounts.light > 0 && <span className="text-[8px] px-1 rounded-full border border-primary/35 bg-primary/10 text-primary/70">{elementCounts.light}</span>}
            </button>
            <button onClick={() => addElement('camera')} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border/30 text-[10px] font-mono text-muted-foreground transition-all hover:bg-primary/10 hover:text-primary hover:border-primary/40">
              <Camera className="w-3 h-3" /> Camera
              {elementCounts.camera > 0 && <span className="text-[8px] px-1 rounded-full border border-primary/35 bg-primary/10 text-primary/70">{elementCounts.camera}</span>}
            </button>
            <button onClick={() => addElement('character')} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border/30 text-[10px] font-mono text-muted-foreground transition-all hover:bg-primary/10 hover:text-primary hover:border-primary/40">
              <User className="w-3 h-3" /> Character
              {elementCounts.character > 0 && <span className="text-[8px] px-1 rounded-full border border-primary/35 bg-primary/10 text-primary/70">{elementCounts.character}</span>}
            </button>
            <span className="ml-auto text-[9px] font-mono text-muted-foreground/40 tracking-wider">AR · EN · Franco</span>
          </div>

          <VisualCanvas
            elements={elements}
            aspectRatio={aspectRatio}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onMove={moveElement}
            onScale={scaleElement}
            globalSettings={globalSettings}
            magneticGuides={magneticGuides}
            conflicts={conflicts}
          />
        </div>

      </div>

      {/* Prompt */}
      <PromptPreview
        prompt={effectivePrompt}
        canvasSubject={canvasSubject}
        onDetectedSubject={setGrokDetectedSubject}
        onSave={saveToHistory}
        conflicts={conflicts}
        inferred={inferred}
        locked={locked || enhanceBusy}
        vfxLayers={vfxLayers}
        vfxIntensity={vfxIntensity}
        styleTendencies={styleTendencies}
        onEnhance={handleEnhance}
        onFixAllIssues={handleFix}
        initialPrompt={sceneDescription}
        onApplySettingsPreset={applySettingsPreset}
        onFixSingleIssue={fixPromptIssueFromText}
        promptFormat={promptFormat}
        onFormatChange={setPromptFormat}
      />
    </div>
  );
};

export default Index;
